/**
 * Review thread picker — compact dropdown listing "Active findings" (merged
 * virtual thread) followed by individual runs, newest first. Hover to
 * dismiss a run with a 10s Undo toast. Footer toggles dismissed visibility.
 */

import { useEffect, useMemo, useState } from 'react'
import { ChevronDown, History, Trash2, RotateCcw } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useReviewStore } from '@/stores/review-store'
import { absoluteTime, relativeTime } from '@/lib/datetime'
import { reviewRunNotice } from '@/lib/review-run-notice'
import type { ReviewRunSummary } from '@/lib/schemas'

const UNDO_TIMEOUT_MS = 10_000

interface ReviewThreadPickerProps {
  artifactId: string
  activeCount: number
}

export function ReviewThreadPicker({ artifactId, activeCount }: ReviewThreadPickerProps) {
  const selectedThreadId = useReviewStore((s) => s.selectedThreadId)
  const runHistory = useReviewStore((s) => s.runHistory)
  const includeDismissed = useReviewStore((s) => s.includeDismissed)
  const historyLoading = useReviewStore((s) => s.historyLoading)
  const loadHistory = useReviewStore((s) => s.loadHistory)
  const selectThread = useReviewStore((s) => s.selectThread)
  const dismissRun = useReviewStore((s) => s.dismissRun)
  const restoreRun = useReviewStore((s) => s.restoreRun)
  const toggleDismissed = useReviewStore((s) => s.toggleDismissed)

  const [undoState, setUndoState] = useState<{ runId: string; timer: number } | null>(null)

  useEffect(() => {
    if (!artifactId) return
    void loadHistory(artifactId)
  }, [artifactId, includeDismissed, loadHistory])

  useEffect(() => {
    return () => {
      if (undoState) window.clearTimeout(undoState.timer)
    }
  }, [undoState])

  const visibleRuns = useMemo(() => {
    if (includeDismissed) return runHistory
    return runHistory.filter((r) => !r.dismissedAt)
  }, [runHistory, includeDismissed])

  const selectedLabel = (() => {
    if (selectedThreadId === 'active') return `Active findings (${activeCount})`
    const run = runHistory.find((r) => r.id === selectedThreadId)
    if (!run) return 'Active findings'
    return summaryLabel(run)
  })()

  const handleDismiss = (runId: string) => {
    void dismissRun(runId)
    const timer = window.setTimeout(() => setUndoState(null), UNDO_TIMEOUT_MS)
    setUndoState({ runId, timer })
  }

  const handleUndo = () => {
    if (!undoState) return
    window.clearTimeout(undoState.timer)
    void restoreRun(undoState.runId)
    setUndoState(null)
  }

  return (
    <div className="flex items-center gap-1">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs gap-1"
            aria-label="Open review history"
          >
            <History className="h-3 w-3" />
            <span className="max-w-[160px] truncate">{selectedLabel}</span>
            <ChevronDown className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuItem
            onClick={() => selectThread('active')}
            className={cn('flex items-center gap-2 text-xs', selectedThreadId === 'active' && 'bg-muted')}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--brand))]" />
            <span className="flex-1 min-w-0 truncate">Active findings</span>
            <span className="text-muted-foreground">{activeCount}</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
            Runs
          </div>
          {historyLoading && visibleRuns.length === 0 ? (
            <div className="px-2 py-1.5 text-meta">Loading…</div>
          ) : visibleRuns.length === 0 ? (
            <div className="px-2 py-1.5 text-meta">No runs yet</div>
          ) : (
            <ul className="max-h-[260px] overflow-y-auto">
              {visibleRuns.map((run) => (
                <li key={run.id} className="group relative">
                  <button
                    type="button"
                    onClick={() => selectThread(run.id)}
                    className={cn(
                      'w-full text-left px-2 py-1.5 text-xs hover:bg-muted rounded-sm flex flex-col gap-0.5',
                      selectedThreadId === run.id && 'bg-muted',
                    )}
                  >
                    <span className="flex items-center gap-1">
                      <span className="font-medium truncate">{summaryLabel(run)}</span>
                      {run.status === 'failed' && (
                        <span className="text-[10px] text-destructive">failed</span>
                      )}
                      {run.dismissedAt && (
                        <span className="text-[10px] text-muted-foreground">dismissed</span>
                      )}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {run.sectionKey ? 'Section' : 'Artifact'}
                      {run.findingCount > 0 && ` · ${severitySummary(run)}`}
                      {' · '}
                      <span title={absoluteTime(run.startedAt)}>{relativeTime(run.startedAt)}</span>
                    </span>
                  </button>
                  <div className="absolute right-1 top-1 opacity-0 group-hover:opacity-100 flex items-center gap-0.5">
                    {run.dismissedAt ? (
                      <button
                        type="button"
                        className="h-6 w-6 flex items-center justify-center rounded hover:bg-background"
                        aria-label="Restore run"
                        onClick={(e) => {
                          e.stopPropagation()
                          void restoreRun(run.id)
                        }}
                      >
                        <RotateCcw className="h-3 w-3" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="h-6 w-6 flex items-center justify-center rounded hover:bg-background text-muted-foreground hover:text-destructive"
                        aria-label="Dismiss run"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDismiss(run.id)
                        }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={(e) => {
              e.preventDefault()
              toggleDismissed()
            }}
            className="text-xs flex items-center gap-2"
          >
            <input
              type="checkbox"
              checked={includeDismissed}
              readOnly
              aria-label="Show dismissed runs"
              className="h-3 w-3"
            />
            Show dismissed runs
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {undoState && (
        <div className="flex items-center gap-1 text-[11px] bg-muted rounded-md px-2 py-1">
          <span>Run dismissed</span>
          <Button size="sm" variant="link" className="h-5 px-1 text-[11px]" onClick={handleUndo}>
            Undo
          </Button>
        </div>
      )}
    </div>
  )
}

function summaryLabel(run: ReviewRunSummary): string {
  if (run.findingCount === 0 && run.status === 'complete') {
    // A run that could not check a criterion found nothing it looked at —
    // that is not the same as clean.
    return reviewRunNotice(run) ? 'Partly checked (0 issues)' : 'Clean run (0 issues)'
  }
  if (run.status === 'failed') return 'Failed run'
  if (run.status === 'running') return 'Running review…'
  return `${run.findingCount} findings`
}

function severitySummary(run: ReviewRunSummary): string {
  const { high, medium, low } = run.severityCounts
  const parts: string[] = []
  if (high > 0) parts.push(`${high} high`)
  if (medium > 0) parts.push(`${medium} medium`)
  if (low > 0) parts.push(`${low} low`)
  return parts.join(', ')
}
